import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Share2,
  Users,
  Hash,
  Send,
  MousePointerClick,
  UserCheck,
  Award,
  RefreshCw,
  CheckCircle2,
  Circle,
  Search,
  Link2Off,
  TrendingUp,
  Wallet,
  Coins,
} from 'lucide-react';
import GrowthTabNav from './GrowthTabNav';
import { getAdminReferralOverview, getAdminTopReferrers } from '../../Services/GrowthService';
import {
  InviteStatus,
  PrivateInvitationAdminRow,
  PrivateReferralStats,
  TopReferrer,
} from '../../Types/GrowthType';
import { formatDateTime } from '../../../Shared/Utils/HelperUtils';
import Pagination from '../../../Shared/Pagination/Pagination';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import GiveCreditsModal, { CreditRecipient } from './GiveCreditsModal';

const PAGE_SIZE = 10;

const labelClass =
  'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';
const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';

const MiniStat = ({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) => (
  <div className="flex items-center justify-between rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-4">
    <span className="flex items-center gap-2 text-xs font-semibold text-slate-400">
      {icon}
      {label}
    </span>
    <span className="text-lg font-black text-slate-100">{value.toLocaleString()}</span>
  </div>
);

const headerClass =
  'px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500';

const STATUS_OPTIONS: Array<{ value: InviteStatus | 'ALL'; label: string }> = [
  { value: 'ALL', label: 'All' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'USED', label: 'Used' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'REVOKED', label: 'Revoked' },
];

const statusBadge = (status?: InviteStatus) => {
  switch ((status || '').toUpperCase()) {
    case 'ACTIVE':
      return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400';
    case 'USED':
      return 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400';
    case 'REVOKED':
      return 'border-amber-500/30 bg-amber-500/10 text-amber-400';
    default:
      return 'border-slate-700 bg-slate-800/60 text-slate-400';
  }
};

const registrationBadge = (status?: string) => {
  switch ((status || '').toUpperCase()) {
    case 'APPROVED':
      return 'border-lime-500/30 bg-lime-500/10 text-lime-400';
    case 'PENDING':
      return 'border-amber-500/30 bg-amber-500/10 text-amber-300';
    default:
      return 'border-slate-700 bg-slate-800/60 text-slate-500';
  }
};

const fullName = (row: {
  firstName?: string | null;
  lastName?: string | null;
}): string => `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();

const inviterName = (row: PrivateInvitationAdminRow) =>
  `${row.invitedByFirstName ?? ''} ${row.invitedByLastName ?? ''}`.trim() ||
  row.invitedByEmail ||
  'Unknown';

/**
 * Renders a credit balance as "remaining/granted". The granted figure is the user's real
 * cumulative total rather than the configured default, so someone topped up above the default
 * still reads as "6/16" instead of the nonsensical "6/10".
 */
const CreditBadge = ({ balance, total }: { balance: number | null; total: number | null }) => {
  const remaining = balance ?? 0;
  const granted = total ?? remaining;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-black ${
        remaining === 0
          ? 'border-rose-500/30 bg-rose-500/10 text-rose-400'
          : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300'
      }`}
    >
      {remaining}
      {granted > 0 && <span className="text-slate-500">/{granted}</span>}
    </span>
  );
};

function GrowthReferralsComponent() {
  const [stats, setStats] = useState<PrivateReferralStats | null>(null);
  const [rows, setRows] = useState<PrivateInvitationAdminRow[]>([]);
  const [pagination, setPagination] = useState<PaginationResponse<PrivateInvitationAdminRow> | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [searchFilter, setSearchFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<InviteStatus | 'ALL'>('ALL');
  const [regFilter, setRegFilter] = useState('');
  const [apprFilter, setApprFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(0);

  // Top Referrers is a full candidate list ranked on all-time data, not a window onto the
  // filtered invite set, so it keeps its own search, page and pagination. Sharing state with the
  // listing would mean filtering to one status silently emptied the referrer table.
  const [referrers, setReferrers] = useState<TopReferrer[]>([]);
  const [referrerPagination, setReferrerPagination] = useState<PaginationResponse<TopReferrer> | null>(
    null
  );
  const [referrerSearch, setReferrerSearch] = useState('');
  const [debouncedReferrerSearch, setDebouncedReferrerSearch] = useState('');
  const [referrerPage, setReferrerPage] = useState(0);
  const [referrersLoading, setReferrersLoading] = useState(true);

  // One modal instance for both tables. The tables show the same person from two angles - the
  // referrer's own row and the inviter column of a link they sent - so the grant target is held
  // as a plain object rather than duplicated state per table.
  const [creditRecipient, setCreditRecipient] = useState<CreditRecipient | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedReferrerSearch(referrerSearch.trim()), 400);
    return () => clearTimeout(timer);
  }, [referrerSearch]);

  useEffect(() => {
    setReferrerPage(0);
  }, [debouncedReferrerSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchFilter.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchFilter]);

  const filters = useMemo(
    () => ({
      status: statusFilter !== 'ALL' ? statusFilter : undefined,
      search: debouncedSearch || undefined,
      isRegistered: regFilter === '' ? undefined : regFilter === 'true',
      isApproved: apprFilter === '' ? undefined : apprFilter === 'true',
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    }),
    [statusFilter, debouncedSearch, regFilter, apprFilter, fromDate, toDate]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAdminReferralOverview(filters, page, PAGE_SIZE);
      if (response.success && response.data) {
        setStats(response.data.stats ?? null);
        const pageData = response.data.page;
        setRows(pageData.content ?? []);
        setPagination(pageData);
        if (pageData.totalPages > 0 && page >= pageData.totalPages) {
          setPage(pageData.totalPages - 1);
        }
      } else {
        setStats(null);
        setRows([]);
        setPagination(null);
        setAlert({
          variant: 'error',
          title: 'Could not load referrals',
          message: response.message || 'Please try again.',
          onClose: () => setAlert(null),
        });
      }
    } catch (err) {
      console.error('Failed to load referrals', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchReferrers = useCallback(async () => {
    setReferrersLoading(true);
    try {
      const response = await getAdminTopReferrers(
        debouncedReferrerSearch || undefined,
        referrerPage,
        PAGE_SIZE
      );
      if (response.success && response.data) {
        setReferrers(response.data.content ?? []);
        setReferrerPagination(response.data);
        if (response.data.totalPages > 0 && referrerPage >= response.data.totalPages) {
          setReferrerPage(response.data.totalPages - 1);
        }
      } else {
        setReferrers([]);
        setReferrerPagination(null);
      }
    } catch (err) {
      console.error('Failed to load top referrers', err);
    } finally {
      setReferrersLoading(false);
    }
  }, [debouncedReferrerSearch, referrerPage]);

  useEffect(() => {
    fetchReferrers();
  }, [fetchReferrers]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter, regFilter, apprFilter, fromDate, toDate]);

  const resetFilters = () => {
    setSearchFilter('');
    setStatusFilter('ALL');
    setRegFilter('');
    setApprFilter('');
    setFromDate('');
    setToDate('');
  };

  const hasActiveFilters =
    Boolean(searchFilter) ||
    statusFilter !== 'ALL' ||
    Boolean(regFilter) ||
    Boolean(apprFilter) ||
    Boolean(fromDate) ||
    Boolean(toDate);

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      <GrowthTabNav />

      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <Share2 size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Referral Program</h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Invite links shared by candidates &amp; who they brought in
          </p>
        </div>
        <button
          onClick={fetchData}
          title="Refresh"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {!stats ? (
        loading ? (
          <div className="flex min-h-[50vh] items-center justify-center text-slate-400">
            <RefreshCw className="h-10 w-10 animate-spin text-cyan-500" />
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-16 text-center text-sm font-semibold text-slate-500">
            Unable to load referral data.
          </div>
        )
      ) : (
        <>
          {/* Filters */}
          <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  placeholder="Search by invite code / inviter / invitee..."
                  className={`${fieldClass} pl-9`}
                />
              </div>
              <div>
                <label className={labelClass}>Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as InviteStatus | 'ALL')}
                  className={fieldClass}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Registered</label>
                <select value={regFilter} onChange={(e) => setRegFilter(e.target.value)} className={fieldClass}>
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Approved</label>
                <select value={apprFilter} onChange={(e) => setApprFilter(e.target.value)} className={fieldClass}>
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>From</label>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={fieldClass} />
              </div>
              <div>
                <label className={labelClass}>To</label>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={fieldClass} />
              </div>
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="rounded-xl border border-slate-800 bg-slate-950 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-rose-400 transition hover:border-rose-500/40"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <MiniStat label="Total Candidates" value={stats.totalCandidates} icon={<Users size={15} className="text-cyan-400" />} />
            <MiniStat label="Candidates w/ Referrals" value={stats.candidatesWithReferrals} icon={<Award size={15} className="text-fuchsia-400" />} />
            <MiniStat label="Total Invitations" value={stats.totalInvitations} icon={<Hash size={15} className="text-amber-400" />} />
            <MiniStat label="Active Invitations" value={stats.activeInvitations} icon={<Send size={15} className="text-emerald-400" />} />
            <MiniStat label="Used Invitations" value={stats.usedInvitations} icon={<MousePointerClick size={15} className="text-sky-400" />} />
            <MiniStat label="Referral Registrations" value={stats.registeredInvitations} icon={<UserCheck size={15} className="text-teal-400" />} />
            <MiniStat label="Referral Approvals" value={stats.approvedInvitations} icon={<CheckCircle2 size={15} className="text-lime-400" />} />
            <MiniStat label="Expired / Revoked" value={stats.expiredInvitations + stats.revokedInvitations} icon={<Link2Off size={15} className="text-slate-400" />} />
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900/60 px-5 py-4 text-xs font-semibold text-slate-400">
            <span className="flex items-center gap-2">
              <TrendingUp size={14} className="text-cyan-400" />
              Conversion {stats.conversionPct.toFixed(1)}%
            </span>
            <span>
              Of links generated became a registration.
            </span>
            <span className="text-slate-600">|</span>
            <span className="flex items-center gap-2">
              <Users size={14} className="text-fuchsia-400" />
              Participation {stats.participationPct.toFixed(1)}%
            </span>
            <span>
              Of the candidate base has shared at least one link.
            </span>
            <span className="text-slate-600">|</span>
            <span className="flex items-center gap-2">
              <Wallet size={14} className="text-cyan-400" />
              Credits are shown as remaining of granted
            </span>
            <span>
              Admin top-ups raise the granted total; revoking an unused link returns the credit.
            </span>
          </div>

          <div className="mb-8 overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
            <div className="flex flex-col gap-3 border-b border-slate-800 bg-slate-900/60 px-5 py-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-sm font-black uppercase tracking-widest text-slate-300">Top Referrers</h3>
                <p className="mt-1 text-[11px] font-semibold text-slate-500">
                  Every candidate who has shared a link, ranked on all-time registrations.
                </p>
              </div>
              <div className="relative w-full md:w-80">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  value={referrerSearch}
                  onChange={(e) => setReferrerSearch(e.target.value)}
                  placeholder="Search candidates by name / email..."
                  className={`${fieldClass} pl-9`}
                />
              </div>
            </div>
            {referrersLoading && referrers.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-5 py-12 text-sm font-semibold text-slate-400">
                <RefreshCw size={16} className="animate-spin text-cyan-500" />
                Loading referrers...
              </div>
            ) : (
            <table className="w-full min-w-[900px] text-left">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/40">
                  {['Candidate', 'Links Shared', 'Balance', 'Still Open', 'Registered', 'Approved', 'Conversion', 'Open Codes', 'Action'].map((h) => (
                    <th key={h} className={headerClass}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {referrers.map((ref, idx) => (
                  <motion.tr
                    key={ref.candidateId}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    className="transition hover:bg-slate-900/70"
                  >
                    <td className="px-5 py-4">
                      <p className="text-sm font-bold text-slate-100">{ref.candidateName || '—'}</p>
                      <p className="text-[11px] font-semibold text-slate-500">{ref.candidateEmail || '—'}</p>
                    </td>
                    <td className="px-5 py-4 text-sm font-semibold text-slate-200">{ref.totalLinks}</td>
                    <td className="px-5 py-4">
                      <CreditBadge balance={ref.inviteBalance} total={ref.inviteTotal} />
                    </td>
                    <td className="px-5 py-4 text-sm font-semibold text-slate-300">{ref.activeLinks}</td>
                    <td className="px-5 py-4 text-sm font-semibold text-slate-300">{ref.registered}</td>
                    <td className="px-5 py-4">
                      <span className="inline-flex rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-bold text-emerald-400">
                        {ref.approved}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm font-black text-cyan-400">{ref.conversionPct.toFixed(1)}%</td>
                    <td className="px-5 py-4">
                      {ref.pendingInviteCodes.length === 0 ? (
                        <span className="text-xs font-semibold text-slate-600">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {ref.pendingInviteCodes.map((code) => (
                            <code
                              key={code}
                              className="rounded-md border border-slate-800 bg-slate-950 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-400"
                            >
                              {code}
                            </code>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() =>
                          setCreditRecipient({
                            userId: ref.candidateId,
                            name: ref.candidateName || 'Candidate',
                            email: ref.candidateEmail,
                            role: 'CANDIDATE',
                            inviteBalance: ref.inviteBalance,
                            inviteTotal: ref.inviteTotal,
                          })
                        }
                        className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest text-amber-400 transition hover:border-amber-400/60 hover:bg-amber-500/20"
                      >
                        <Coins size={11} />
                        Give Credits
                      </button>
                    </td>
                  </motion.tr>
                ))}
                {referrers.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-5 py-12 text-center text-sm font-semibold text-slate-500">
                      {debouncedReferrerSearch
                        ? 'No candidates match that search.'
                        : 'No referral activity yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            )}
          </div>

          {referrerPagination && referrerPagination.totalElements > 0 && (
            <div className="-mt-6 mb-8 rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
              <Pagination
                page={referrerPagination.page}
                size={referrerPagination.size}
                totalElements={referrerPagination.totalElements}
                totalPages={referrerPagination.totalPages}
                first={referrerPagination.first}
                last={referrerPagination.last}
                hasNext={referrerPagination.hasNext}
                hasPrevious={referrerPagination.hasPrevious}
                setPage={setReferrerPage}
              />
            </div>
          )}

          <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
            <div className="border-b border-slate-800 bg-slate-900/60 px-5 py-4">
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-300">Recent Invitations</h3>
            </div>
            <table className="w-full min-w-[1100px] text-left">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/40">
                  {['Invited By', 'Invitee', 'Invite Code', 'Status', 'Registration', 'Links / Balance', 'Created', 'Expires', 'Action'].map((h) => (
                    <th key={h} className={headerClass}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {rows.map((ref, idx) => (
                  <motion.tr
                    key={ref.invitationId ?? ref.inviteCode}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.02 }}
                    className="transition hover:bg-slate-900/70"
                  >
                    <td className="px-5 py-4">
                      <p className="text-sm font-bold text-slate-100">{inviterName(ref)}</p>
                      <p className="text-[11px] font-semibold text-slate-500">{ref.invitedByEmail || '—'}</p>
                      <button
                        onClick={() =>
                          setCreditRecipient({
                            userId: ref.invitedById,
                            name: inviterName(ref),
                            email: ref.invitedByEmail,
                            role: ref.invitedByRole ?? null,
                            inviteBalance: ref.invitedByInviteBalance,
                            inviteTotal: ref.invitedByInviteTotal,
                          })
                        }
                        className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest text-amber-400 transition hover:border-amber-400/60 hover:bg-amber-500/20"
                      >
                        <Coins size={11} />
                        Give Credits
                      </button>
                    </td>                    <td className="px-5 py-4">
                      {ref.registrationStatus === 'REGISTERED' ? (
                        <>
                          <p className="text-sm font-bold text-slate-100">{fullName(ref) || '—'}</p>
                          <p className="text-[11px] font-semibold text-slate-500">{ref.email || '—'}</p>
                        </>
                      ) : (
                        <span className="text-xs font-semibold italic text-slate-600">Not registered yet</span>
                      )}
                    </td>
                    <td className="px-5 py-4 font-mono text-xs font-bold text-cyan-400">{ref.inviteCode}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${statusBadge(
                          ref.invitationStatus
                        )}`}
                      >
                        {ref.invitationStatus}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${registrationBadge(
                          ref.approvalStatus
                        )}`}
                      >
                        {ref.approvalStatus === 'APPROVED' ? (
                          <CheckCircle2 size={11} />
                        ) : ref.approvalStatus === 'PENDING' ? (
                          <Circle size={11} />
                        ) : (
                          <Circle size={11} className="text-slate-600" />
                        )}
                        {ref.approvalStatus === 'NOT_REGISTERED' ? 'No signup' : ref.approvalStatus}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-sm font-black text-slate-200">
                        {ref.invitedByTotalLinks ?? '—'}
                        <span className="ml-1 text-[11px] font-semibold text-slate-500">total</span>
                      </p>
                      <p
                        className={`mt-1 inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-xs font-black ${
                          (ref.invitedByInviteBalance ?? 0) === 0
                            ? 'border-rose-500/30 bg-rose-500/10 text-rose-400'
                            : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300'
                        }`}
                      >
                        <Wallet size={11} />
                        {ref.invitedByInviteBalance ?? 0}
                        {ref.invitedByInviteTotal != null && ref.invitedByInviteTotal > 0 && (
                          <span className="text-slate-500">/{ref.invitedByInviteTotal} left</span>
                        )}
                      </p>
                    </td>
                    <td className="px-5 py-4 text-xs font-semibold text-slate-500">{formatDateTime(ref.createdAt ?? undefined)}</td>
                    <td className="px-5 py-4 text-xs font-semibold text-slate-500">{formatDateTime(ref.expiryAt ?? undefined)}</td>
                  </motion.tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-5 py-12 text-center text-sm font-semibold text-slate-500">
                      {hasActiveFilters ? 'No invitations match these filters.' : 'No referral invitations yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalElements > 0 && (
            <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
              <Pagination
                page={pagination.page}
                size={pagination.size}
                totalElements={pagination.totalElements}
                totalPages={pagination.totalPages}
                first={pagination.first}
                last={pagination.last}
                hasNext={pagination.hasNext}
                hasPrevious={pagination.hasPrevious}
                setPage={setPage}
              />
            </div>
          )}
        </>
      )}

      {/*
        One instance for both tables. A successful grant refetches both, so the balance column and
        the granted total update immediately rather than on the next manual refresh.
      */}
      <GiveCreditsModal
        recipient={creditRecipient}
        onClose={() => setCreditRecipient(null)}
        onGranted={(message) => {
          setAlert({
            variant: 'success',
            title: 'Credits added',
            message,
            onClose: () => setAlert(null),
          });
          fetchData();
          fetchReferrers();
        }}
        onError={(title, message) =>
          setAlert({ variant: 'error', title, message, onClose: () => setAlert(null) })
        }
      />
    </div>
  );
}

export default GrowthReferralsComponent;
